import {
  Store,
  ArrowRight,
  Phone,
  LogOut,
  Settings,
  MapPin,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useQuery } from "react-query";
import { cn } from "@/lib/utils";
import { useOrganization } from "@/context/organization.context";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/auth.context";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { toast } from "sonner";
import authService from "@/services/auth.service";
import { useTranslation } from "react-i18next";
import useSettings from "@/hooks/useSettings";

export default function ChooseOutlets() {
  const { activeOrg } = useOrganization();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation("chooseOutlet");

  const { data: outlets = [], isLoading } = useQuery(
    ["outlets", activeOrg?.id],
    async () => {
      const { data: outlets } = await api.get("/pos-outlets");
      return outlets.results;
    },
  );
  const { memberShip } = useOrganization();
  const { settings } = useSettings();

  const outletsToShow = outlets?.filter((outlet) => {
    const isActive = outlet.status === "active";
    if (settings?.allow_open_outlet_access) return isActive;
    return (
      outlet?.users?.find((user) => user?.memberShip?.id === memberShip?.id) &&
      isActive
    );
  });

  const filteredOutlets = outletsToShow?.filter(
    (outlet) =>
      outlet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      outlet.location?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate("/login");
    } catch (error) {
      toast.error(t("errors.logoutFailed"));
    }
  };

  const getInitials = (name: string) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding & User Info */}
      <div className="hidden lg:flex lg:w-[440px] bg-primaryDark flex-col p-8 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="grid"
                width="32"
                height="32"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 32 0 L 0 0 0 32"
                  fill="none"
                  stroke="white"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full">
          <Link to={"/"} className="mb-10 flex">
            <img className="h-7" src="/logo_white.png" alt="" />
          </Link>

          {/* Organization Info */}
          <div className="mb-4">
            <p className="text-primary-foreground/60 text-sm font-medium uppercase tracking-wider mb-4">
              {t("currentOrganization")}
            </p>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-md bg-card/20 flex items-center justify-center">
                <Store className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base truncate">
                  {activeOrg?.name || "Organization"}
                </h3>
                <p className="text-white/70 text-sm truncate">
                  {t("outlet", { count: outletsToShow?.length || 0 })}
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-2"></div>

          {/* User Info */}
          <div className="mb-4 mt-4">
            <p className="text-primary-foreground/60 text-sm font-medium uppercase tracking-wider mb-4">
              {t("loggedInAs")}
            </p>
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12 border-[3px] border-white/30 ring-2 ring-white/10">
                <AvatarImage src={user?.photo} />
                <AvatarFallback className="bg-card text-primaryDark text-xl font-semibold">
                  {getInitials(user?.names || user?.email)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base truncate">
                  {user?.name || "User"}
                </h3>
                <p className="text-white/70 text-sm truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-2"></div>

          {/* Action Links */}
          <div className="space-y-3 mt-4 flex-1">
            <button
              onClick={() => navigate("/choose-module")}
              className="w-full border border-slate-100/20 flex items-center gap-3 px-4 py-3.5 rounded-md text-white hover:bg-card/10 transition-colors text-left group"
            >
              <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                <Settings className="h-4 w-4" />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="font-medium text-sm leading-4">
                  {t("dashboard")}
                </span>
                <span className="text-white/60 text-xs">
                  {t("backToMainDashboard")}
                </span>
              </div>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center border border-slate-100/20 gap-3 px-4 py-3.5 rounded-md text-white hover:bg-card/10 bg-card/5 transition-colors text-left group"
            >
              <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                <LogOut className="h-4 w-4" />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="font-medium text-sm leading-4">
                  {t("signOut")}
                </span>
                <span className="text-white/60 text-xs">{t("endSession")}</span>
              </div>
            </button>
          </div>

          {/* Footer */}
          <div className="pt-6 mt-auto">
            <p className="text-white/40 text-sm">
              {t("footerRights", { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Outlet Selection */}
      <div className="flex-1 flex flex-col bg-backgroud">
        {/* Mobile Header */}
        <header className="flex items-center justify-between px-4 py-3 bg-card border-b border-border lg:hidden">
          <Link to={"/"}>
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/choose-module")}
              className="rounded-md"
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-md text-muted-foreground"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Mobile User Info */}
        <div className="px-4 py-4 bg-card border-b border-border lg:hidden">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.photo} />
              <AvatarFallback className="bg-primary/10 text-primary font-medium">
                {getInitials(user?.names || user?.email)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-muted-foreground text-xs">{activeOrg?.name}</p>
              <p className="font-medium text-foreground truncate">
                {user?.names || "User"}
              </p>
            </div>
            <Badge variant="outline" className="rounded-md text-xs">
              {t("outlet", { count: outletsToShow?.length || 0 })}
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl mx-auto">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-foreground mb-1">
                {t("title")}
              </h2>
              <p className="text-muted-foreground text-sm">{t("subtitle")}</p>
            </div>

            {/* Search */}
            {outletsToShow?.length > 3 && (
              <div className="relative mb-5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foregroun" />
                <Input
                  placeholder={t("search.placeholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-md border-border bg-card h-11"
                />
              </div>
            )}

            {/* Loading State */}
            {isLoading && <POSOutletSkeleton />}

            {/* Empty State */}
            {outletsToShow?.length === 0 && !isLoading && <POSOutletEmpty />}

            {/* Outlets Grid */}
            {!isLoading && filteredOutlets?.length > 0 && (
              <div className="grid gap-3">
                {filteredOutlets.map((outlet, index) => (
                  <OutletCard key={outlet.id} outlet={outlet} index={index} />
                ))}
              </div>
            )}

            {/* No results from search */}
            {!isLoading &&
              filteredOutlets?.length === 0 &&
              outletsToShow?.length > 0 && (
                <div className="text-center py-12 bg-card rounded-md border border-border">
                  <Search className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    {t("search.noResults")}
                  </p>
                  <p className="text-muted-foregroun text-sm">
                    {t("search.tryDifferent")}
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* Mobile Footer */}
        <footer className="px-4 py-3 border-t border-border bg-card lg:hidden">
          <p className="text-center text-muted-foregroun text-xs">
            {t("footerRights", { year: new Date().getFullYear() })}
          </p>
        </footer>
      </div>
    </div>
  );
}

function OutletCard({ outlet, index }: { outlet: any; index: number }) {
  const navigate = useNavigate();
  const { t } = useTranslation("chooseOutlet");

  const colors = [
    "#6366f1",
    "#8b5cf6",
    "#ec4899",
    "#f43f5e",
    "#f97316",
    "#eab308",
    "#22c55e",
    "#14b8a6",
  ];

  return (
    <button
      onClick={() => {
        if (outlet.status === "active") {
          navigate(`/sales/pos/${outlet.id}`);
        }
      }}
      disabled={outlet.status !== "active"}
      className={cn(
        "w-full p-4 rounded-md border border-border bg-card text-left transition-all group",
        outlet.status === "active"
          ? "hover:border-primary hover:ring-2 hover:ring-primary/10 cursor-pointer"
          : "opacity-60 cursor-not-allowed",
      )}
    >
      <div className="flex items-center gap-4">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-md text-white flex-shrink-0"
          style={{ backgroundColor: colors[index % colors.length] }}
        >
          <Store className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-sm text-foreground truncate">
              {outlet.name}
            </h3>
            <Badge
              className={cn(
                "text-xs px-2 py-0.5 rounded-md",
                outlet.status === "active" &&
                  "bg-green-50 text-green-700 border-green-200",
                outlet.status === "inactive" &&
                  "bg-red-50 text-red-700 border-red-200",
                outlet.status === "pending" &&
                  "bg-orange-50 text-orange-700 border-orange-200",
              )}
            >
              {t(`status.${outlet.status}`)}
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {outlet.location && (
              <span className="flex items-center gap-1 truncate">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                {outlet.location}
              </span>
            )}
          </div>
        </div>
        {outlet.status === "active" && (
          <div className="flex items-center justify-center h-8 w-8 rounded-md bg-accent/50 group-hover:bg-primary group-hover:text-white transition-colors">
            <ArrowRight className="h-4 w-4" />
          </div>
        )}
      </div>
    </button>
  );
}

function POSOutletSkeleton() {
  return (
    <div className="grid gap-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 bg-card border border-border rounded-md">
          <div className="flex items-center gap-4">
            <Skeleton className="h-12 w-12 rounded-md" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-16 rounded-md" />
              </div>
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-8 w-8 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
}

function POSOutletEmpty() {
  const navigate = useNavigate();
  const { t } = useTranslation("chooseOutlet");

  return (
    <div className="text-center py-16 px-6 bg-card rounded-md border border-border">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-md bg-accent/50">
        <Store className="h-8 w-8 text-muted-foregroun" />
      </div>
      <h2 className="text-base font-semibold text-foreground mb-2">
        {t("emptyState.title")}
      </h2>
      <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
        {t("emptyState.description")}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button onClick={() => navigate("/contact")} className="rounded-md">
          <Phone className="mr-2 h-4 w-4" />
          {t("emptyState.contactSupport")}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/choose-module")}
          className="rounded-md"
        >
          {t("emptyState.backToDashboard")}
        </Button>
      </div>
    </div>
  );
}
